import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Clock, 
  TrendingUp, 
  TrendingDown, 
  Calendar,
  Download,
  RefreshCw,
  DollarSign,
  BarChart3,
  Target,
  AlertTriangle,
  CheckCircle,
  Brain
} from 'lucide-react';

// Utility function to format large numbers
const formatCurrency = (value) => {
  if (value >= 1000000) {
    return `₺${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `₺${(value / 1000).toFixed(0)}k`;
  } else {
    return `₺${value.toLocaleString()}`;
  }
};

export default function HourlyPerformance() {
  const { selectedBrand } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [isLoading, setIsLoading] = useState(false);
  const [hourlyData, setHourlyData] = useState([]);
  const [hourlyTrendsData, setHourlyTrendsData] = useState([]);
  const [historicalData, setHistoricalData] = useState([]);
  const [isLoadingTrends, setIsLoadingTrends] = useState(false);

  // Mock data generator for hourly performance
  const generateHourlyData = () => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const currentHour = 17; // Demo: Current time is 17:00
    
    return hours.map(hour => {
      // Only generate data up to current hour (17:00)
      if (hour > currentHour) {
        return {
          hour: `${hour.toString().padStart(2, '0')}:00`,
          hourNum: hour,
          backOfficeRevenue: null,
          analyticsRevenue: null,
          googleAdsCost: null,
          metaAdsCost: null,
          hasData: false
        };
      }
      
      // Back Office: Günlük 5M hedefle (saatlik ortalama ~208k)
      // Gece saatleri daha düşük, gündüz saatleri daha yüksek
      let backOfficeMultiplier = 1;
      if (hour >= 0 && hour <= 6) backOfficeMultiplier = 0.3;      // Gece: düşük
      else if (hour >= 7 && hour <= 10) backOfficeMultiplier = 0.8; // Sabah: orta
      else if (hour >= 11 && hour <= 17) backOfficeMultiplier = 1.4; // Gündüz: yüksek
      else if (hour >= 18 && hour <= 22) backOfficeMultiplier = 1.1; // Akşam: orta-yüksek
      else backOfficeMultiplier = 0.5; // Gece: düşük
      
      const baseBackOffice = 200000 + (Math.random() * 150000); // 200k-350k base (artırıldı)
      const backOfficeRevenue = Math.floor(baseBackOffice * backOfficeMultiplier);
      
      // Analytics revenue should be much smaller than back office (15-30% of back office)
      const analyticsMultiplier = 0.15 + (Math.random() * 0.15); // 15%-30% of back office (artırıldı)
      const analyticsRevenue = Math.floor(backOfficeRevenue * analyticsMultiplier);
      
      return {
        hour: `${hour.toString().padStart(2, '0')}:00`,
        hourNum: hour,
        backOfficeRevenue: backOfficeRevenue,                    // Günlük ~6M+ hedef
        analyticsRevenue: analyticsRevenue,                      // Back office'in %15-30'u
        googleAdsCost: Math.floor(Math.random() * 3500) + 2000,      // 2k-5.5k (artırıldı, günlük ~85k hedef)
        metaAdsCost: Math.floor(Math.random() * 2500) + 300,          // 300-2.8k
        hasData: true
      };
    });
  };

  // Generate historical hourly data for trend analysis (last 7 days)
  const generateHistoricalHourlyData = () => {
    const historicalData = [];
    const currentHour = 17; // Demo: Current time is 17:00
    
    // Generate data for last 7 days
    for (let dayOffset = 6; dayOffset >= 0; dayOffset--) {
      const date = new Date();
      date.setDate(date.getDate() - dayOffset);
      
      const dayOfWeek = date.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const weekendMultiplier = isWeekend ? 0.7 : 1.0;
      
      // For past days, generate full 24 hours. For today, only up to current hour
      const maxHour = dayOffset === 0 ? currentHour : 23;
      
      // Generate hourly data for this day
      const dayData = Array.from({ length: maxHour + 1 }, (_, hour) => {
        // Hour-specific multipliers (same pattern as current hourly data)
        let hourMultiplier = 1;
        if (hour >= 0 && hour <= 6) hourMultiplier = 0.3;
        else if (hour >= 7 && hour <= 10) hourMultiplier = 0.8;
        else if (hour >= 11 && hour <= 17) hourMultiplier = 1.4;
        else if (hour >= 18 && hour <= 22) hourMultiplier = 1.1;
        else hourMultiplier = 0.5;
        
        // Add some day-to-day volatility
        const volatilityFactor = 0.85 + (Math.random() * 0.3); // ±15% volatility
        
        const baseBackOffice = 200000 + (Math.random() * 150000); // 200k-350k base (artırıldı)
        const backOfficeRevenue = Math.floor(baseBackOffice * hourMultiplier * weekendMultiplier * volatilityFactor);
        
        // Analytics revenue should be much smaller than back office (15-30% of back office)
        const analyticsMultiplier = 0.15 + (Math.random() * 0.15); // 15%-30% of back office (artırıldı)
        const analyticsRevenue = Math.floor(backOfficeRevenue * analyticsMultiplier);
        const totalRevenue = backOfficeRevenue + analyticsRevenue;
        
        const googleAdsCost = Math.floor((Math.random() * 3500) + 2000) * hourMultiplier * volatilityFactor;
        const metaAdsCost = Math.floor((Math.random() * 2500) + 300) * hourMultiplier * volatilityFactor;
        const totalCost = googleAdsCost + metaAdsCost;
        
        const roas = totalRevenue / totalCost;
        
        return {
          date: date.toISOString().split('T')[0],
          hour,
          hourDisplay: `${hour.toString().padStart(2, '0')}:00`,
          backOfficeRevenue,
          analyticsRevenue,
          totalRevenue,
          googleAdsCost,
          metaAdsCost,
          totalCost,
          roas: parseFloat(roas.toFixed(2)),
          dayOfWeek: date.toLocaleDateString('en-US', { weekday: 'short' })
        };
      });
      
      historicalData.push(...dayData);
    }
    
    return historicalData;
  };

  // Calculate hourly trends and generate AI recommendations
  const calculateHourlyTrendsAndRecommendations = (historicalData, currentHourlyData) => {
    // Group historical data by hour
    const hourlyGroups = {};
    
    for (let hour = 0; hour < 24; hour++) {
      hourlyGroups[hour] = historicalData.filter(d => d.hour === hour);
    }
    
    // Analyze each hour and generate recommendations
    return currentHourlyData.map(currentHour => {
      const hour = currentHour.hourNum;
      const historicalForThisHour = hourlyGroups[hour] || [];
      
      // Calculate historical ROAS for all hours (from past days)
      const historicalAvgRoas = historicalForThisHour.length >= 2 ? 
        (historicalForThisHour.slice(-2).reduce((sum, d) => sum + d.roas, 0) / 2).toFixed(2) : 'N/A';
      
      // Skip AI analysis for hours without current data, but keep historical data
      if (!currentHour.hasData) {
        return {
          ...currentHour,
          trend: null,
          recommendation: null,
          recommendationType: null,
          historicalAvgRoas
        };
      }
      
      let trend = 'stable';
      let recommendation = 'Maintain current budget';
      let recommendationType = 'maintain';
      
      if (historicalForThisHour.length >= 4) { // Need at least 4 days of data
        // Compare last 2 days vs previous 2 days for this specific hour
        const sortedData = historicalForThisHour.sort((a, b) => new Date(a.date) - new Date(b.date));
        
        if (sortedData.length >= 4) {
          const recent2Days = sortedData.slice(-2);
          const previous2Days = sortedData.slice(-4, -2);
          
          const recentAvgRoas = recent2Days.reduce((sum, d) => sum + d.roas, 0) / recent2Days.length;
          const previousAvgRoas = previous2Days.reduce((sum, d) => sum + d.roas, 0) / previous2Days.length;
          
          const roasChange = ((recentAvgRoas - previousAvgRoas) / previousAvgRoas) * 100;
          
          // Simple ROAS-based recommendations
          if (recentAvgRoas >= 6) {
            trend = 'strong_increasing';
            recommendation = `Scale budget +25%`;
            recommendationType = 'increase';
          } else if (recentAvgRoas >= 4) {
            trend = 'increasing';
            recommendation = `Increase budget +15%`;
            recommendationType = 'increase';
          } else if (recentAvgRoas >= 2.5) {
            if (roasChange > 10) {
              trend = 'increasing';
              recommendation = `Increase budget +10%`;
              recommendationType = 'increase';
            } else if (roasChange < -10) {
              trend = 'decreasing';
              recommendation = `Reduce budget -10%`;
              recommendationType = 'decrease';
            } else {
              trend = 'stable';
              recommendation = `Maintain budget`;
              recommendationType = 'maintain';
            }
          } else {
            trend = 'strong_decreasing';
            recommendation = `Reduce budget -25%`;
            recommendationType = 'decrease';
          }
          
          // Add peak hour bonus
          if (hour >= 11 && hour <= 17 && recentAvgRoas >= 3) {
            recommendation += ` (Peak hour advantage)`;
          }
        }
      }
      
      return {
        ...currentHour,
        trend,
        recommendation,
        recommendationType,
        historicalAvgRoas
      };
    });
  };

  useEffect(() => {
    setIsLoading(true);
    setIsLoadingTrends(true);
    
    // Simulate API calls
    setTimeout(() => {
      const currentHourlyData = generateHourlyData();
      const historical = generateHistoricalHourlyData();
      
      setHourlyData(currentHourlyData);
      setHistoricalData(historical);
      setIsLoading(false);
      
      // Calculate hourly trends with historical data
      const trendsWithRecommendations = calculateHourlyTrendsAndRecommendations(historical, currentHourlyData);
      setHourlyTrendsData(trendsWithRecommendations);
      setIsLoadingTrends(false);
    }, 1200);
  }, [selectedDate]);

  const handleRefresh = () => {
    setIsLoading(true);
    setIsLoadingTrends(true);
    
    setTimeout(() => {
      const currentHourlyData = generateHourlyData();
      const historical = generateHistoricalHourlyData();
      
      setHourlyData(currentHourlyData);
      setHistoricalData(historical);
      setIsLoading(false);
      
      // Calculate hourly trends with historical data
      const trendsWithRecommendations = calculateHourlyTrendsAndRecommendations(historical, currentHourlyData);
      setHourlyTrendsData(trendsWithRecommendations);
      setIsLoadingTrends(false);
    }, 1200);
  };

  // Calculate totals for summary cards
  const totals = hourlyData.reduce((acc, data) => {
    // Only include hours with data
    if (data.hasData) {
      return {
        backOfficeRevenue: acc.backOfficeRevenue + data.backOfficeRevenue,
        analyticsRevenue: acc.analyticsRevenue + data.analyticsRevenue,
        googleAdsCost: acc.googleAdsCost + data.googleAdsCost,
        metaAdsCost: acc.metaAdsCost + data.metaAdsCost
      };
    }
    return acc;
  }, { backOfficeRevenue: 0, analyticsRevenue: 0, googleAdsCost: 0, metaAdsCost: 0 });

  const totalRevenue = totals.backOfficeRevenue + totals.analyticsRevenue;
  const totalCost = totals.googleAdsCost + totals.metaAdsCost;
  const totalProfit = totalRevenue - totalCost;
  const profitMargin = totalRevenue > 0 ? ((totalProfit / totalRevenue) * 100).toFixed(1) : 0;

        return (
     <div className="min-h-screen bg-[#f6f8ff]/70 p-6">
       <div className="max-w-7xl mx-auto">
         {/* Header */}
         <div className="mb-8">
           <div className="flex items-center justify-between">
             <div>
                             <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Brain className="w-8 h-8 text-purple-600" />
                Complete Hourly Performance Dashboard
                <span className="ml-3 px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  Live Data Collection
                </span>
              </h1>
              <p className="text-gray-600 mt-2">
                Comprehensive hourly analytics with AI-powered budget optimization recommendations for {selectedBrand?.name}
                <span className="block text-sm text-green-600 mt-1">
                  Data syncing every hour • Next update: 18:00 • 7 hours pending
                </span>
              </p>
             </div>
             <div className="flex items-center gap-4">
               <button
                 onClick={handleRefresh}
                 disabled={isLoading}
                 className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
               >
                 <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                 Refresh
               </button>
               <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                 <Download className="w-4 h-4" />
                 Export
               </button>
             </div>
           </div>
         </div>

         {/* Controls */}
         <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
           <div className="flex items-center justify-between">
             <div className="flex items-center gap-6">
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-2">
                   <Calendar className="w-4 h-4 inline mr-2" />
                   Date
                 </label>
                 <input
                   type="date"
                   value={selectedDate}
                   onChange={(e) => setSelectedDate(e.target.value)}
                   className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                 />
               </div>
             </div>
             <div className="text-sm text-gray-500">
               Last updated: {new Date().toLocaleTimeString()} • Current Hour: 17:00
             </div>
           </div>
         </div>

         

                 {/* Combined Hourly Performance & AI Optimization */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-600" />
              Complete Hourly Performance & AI Optimization
              <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                {selectedDate}
              </span>
            </h3>
            <div className="flex items-center gap-6 text-xs text-gray-600 mt-3">
              <div className="flex items-center gap-1">
                <span>ROAS ≥6x: Scale +25%</span>
              </div>
              <div className="flex items-center gap-1">
                <span>ROAS 4-6x: Scale +15%</span>
              </div>
              <div className="flex items-center gap-1">
                <span>ROAS &lt;2.5x: Reduce -25%</span>
              </div>
              <div className="flex items-center gap-1">
                <span>Peak Hour (11-17)</span>
              </div>
            </div>
          </div>

        {/* Performance Summary */}
        {!isLoadingTrends && hourlyTrendsData.length > 0 && (
          <div className="grid grid-cols-4 gap-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 border-b border-gray-200">
            <div className="bg-white rounded-lg p-3 border border-red-200 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center">
                  <img src="/google_ads-512.webp" alt="Google Ads" className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-xs text-red-600 font-medium">Google Ads Total</p>
                  <p className="text-lg font-bold text-red-700">
                    {formatCurrency(totals.googleAdsCost)}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-3 border border-purple-200 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center">
                  <img src="/6033716.png" alt="Meta" className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-xs text-purple-600 font-medium">Meta Ads Total</p>
                  <p className="text-lg font-bold text-purple-700">
                    {formatCurrency(totals.metaAdsCost)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-3 border border-green-200 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-green-600 rounded flex items-center justify-center">
                  <span className="text-white text-xs font-bold">B</span>
                </div>
                <div>
                  <p className="text-xs text-green-600 font-medium">Back Office Total</p>
                  <p className="text-lg font-bold text-green-700">
                    {formatCurrency(totals.backOfficeRevenue)}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-3 border border-blue-200 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center">
                  <img src="/GA4logoicon.svg" alt="Analytics" className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-xs text-blue-600 font-medium">Analytics Total</p>
                  <p className="text-lg font-bold text-blue-700">
                    {formatCurrency(totals.analyticsRevenue)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
                     
          {(isLoading || isLoadingTrends) ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-max">
                <thead className="bg-gradient-to-r from-purple-50 to-blue-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-900 sticky left-0 bg-purple-50 z-10 min-w-24">
                      Metric
                    </th>
                    {hourlyData.map((data, index) => {
                      const isPeakHour = data.hourNum >= 11 && data.hourNum <= 17;
                      return (
                        <th key={data.hour} className={`px-2 py-2 text-center text-xs font-semibold text-gray-900 min-w-20 ${isPeakHour ? 'bg-yellow-100' : ''}`}>
                          <div className="flex flex-col items-center">
                            <span className="font-mono">{data.hour}</span>
                            {isPeakHour && <span className="text-yellow-600 text-xs font-bold">PEAK</span>}
                          </div>
                        </th>
                      );
                    })}
                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-900 bg-gray-100 min-w-20">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {/* Back Office Revenue Row */}
                  <tr className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 text-xs font-semibold text-green-700 sticky left-0 bg-white z-10 border-r border-gray-200">
                      Back Office Revenue
                    </td>
                    {hourlyData.map((data) => (
                      <td key={`back-office-${data.hour}`} className={`px-2 py-2 text-center text-xs font-medium ${data.hasData ? 'text-green-700' : 'text-gray-300'}`}>
                        {data.hasData ? `${formatCurrency(data.backOfficeRevenue)}` : '-'}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-center text-xs font-bold text-green-700 bg-gray-50">
                      {formatCurrency(totals.backOfficeRevenue)}
                    </td>
                  </tr>
                  
                  {/* Back Office 2d Average Row */}
                  <tr className="border-b border-gray-100 hover:bg-gray-50 bg-green-50/30">
                    <td className="px-3 py-2 text-xs font-semibold text-emerald-700 sticky left-0 bg-green-50 z-10 border-r border-gray-200">
                      Back Office 2d Avg
                    </td>
                    {hourlyData.map((data, index) => {
                      // Generate 2d average for back office revenue (slightly different from current)
                      if (!data.hasData) {
                        return (
                          <td key={`back-office-avg-${data.hour}`} className="px-2 py-2 text-center text-xs text-emerald-700">
                            {(() => {
                              const baseBackOffice = 200000 + (Math.random() * 150000);
                              let hourMultiplier = 1;
                              if (data.hourNum >= 0 && data.hourNum <= 6) hourMultiplier = 0.3;
                              else if (data.hourNum >= 7 && data.hourNum <= 10) hourMultiplier = 0.8;
                              else if (data.hourNum >= 11 && data.hourNum <= 17) hourMultiplier = 1.4;
                              else if (data.hourNum >= 18 && data.hourNum <= 22) hourMultiplier = 1.1;
                              else hourMultiplier = 0.5;
                              
                              const avgBackOffice = Math.floor(baseBackOffice * hourMultiplier * 0.92); // Slightly lower than current
                              return formatCurrency(avgBackOffice);
                            })()}
                          </td>
                        );
                      }
                      
                      // For hours with data, show 2d average (slightly different from current)
                      const avgBackOffice = Math.floor(data.backOfficeRevenue * (0.88 + Math.random() * 0.24)); // 88-112% of current
                      return (
                        <td key={`back-office-avg-${data.hour}`} className="px-2 py-2 text-center text-xs font-medium text-emerald-700">
                          {formatCurrency(avgBackOffice)}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-center text-xs font-bold text-emerald-700 bg-green-100">
                      {(() => {
                        let totalAvg = 0;
                        hourlyData.forEach((data) => {
                          if (data.hasData) {
                            totalAvg += Math.floor(data.backOfficeRevenue * (0.88 + Math.random() * 0.24));
                          }
                        });
                        return formatCurrency(totalAvg);
                      })()}
                    </td>
                  </tr>
                  
                  {/* Analytics Revenue Row */}
                  <tr className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 text-xs font-semibold text-blue-700 sticky left-0 bg-white z-10 border-r border-gray-200">
                      Analytics Revenue
                    </td>
                    {hourlyData.map((data) => (
                      <td key={`analytics-${data.hour}`} className={`px-2 py-2 text-center text-xs font-medium ${data.hasData ? 'text-blue-700' : 'text-gray-300'}`}>
                        {data.hasData ? `${formatCurrency(data.analyticsRevenue)}` : '-'}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-center text-xs font-bold text-blue-700 bg-gray-50">
                      {formatCurrency(totals.analyticsRevenue)}
                    </td>
                  </tr>
                  
                  {/* Google Ads Cost Row */}
                  <tr className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 text-xs font-semibold text-red-700 sticky left-0 bg-white z-10 border-r border-gray-200">
                      Google Ads Cost
                    </td>
                    {hourlyData.map((data) => (
                      <td key={`google-${data.hour}`} className={`px-2 py-2 text-center text-xs font-medium ${data.hasData ? 'text-red-700' : 'text-gray-300'}`}>
                        {data.hasData ? `${formatCurrency(data.googleAdsCost)}` : '-'}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-center text-xs font-bold text-red-700 bg-gray-50">
                      {formatCurrency(totals.googleAdsCost)}
                    </td>
                  </tr>
                  
                  {/* Meta Ads Cost Row */}
                  <tr className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 text-xs font-semibold text-purple-700 sticky left-0 bg-white z-10 border-r border-gray-200">
                      Meta Ads Cost
                    </td>
                    {hourlyData.map((data) => (
                      <td key={`meta-${data.hour}`} className={`px-2 py-2 text-center text-xs font-medium ${data.hasData ? 'text-purple-700' : 'text-gray-300'}`}>
                        {data.hasData ? `${formatCurrency(data.metaAdsCost)}` : '-'}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-center text-xs font-bold text-purple-700 bg-gray-50">
                      {formatCurrency(totals.metaAdsCost)}
                    </td>
                  </tr>
                  
                  {/* Current ROAS Row */}
                  <tr className="border-b border-gray-100 hover:bg-gray-50 bg-blue-50/30">
                    <td className="px-3 py-2 text-xs font-semibold text-indigo-700 sticky left-0 bg-blue-50 z-10 border-r border-gray-200">
                      Current ROAS
                    </td>
                    {hourlyData.map((data, index) => {
                      if (!data.hasData) {
                        return (
                          <td key={`current-roas-${data.hour}`} className="px-2 py-2 text-center text-xs text-gray-300">
                            -
                          </td>
                        );
                      }
                      const currentRoas = (data.backOfficeRevenue + data.analyticsRevenue) / (data.googleAdsCost + data.metaAdsCost);
                      return (
                        <td key={`current-roas-${data.hour}`} className="px-2 py-2 text-center text-xs font-bold">
                          <span className={`${currentRoas >= 5 ? 'text-green-600' : currentRoas >= 3 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {currentRoas.toFixed(1)}x
                          </span>
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-center text-xs font-bold text-indigo-700 bg-blue-100">
                      {totals.googleAdsCost + totals.metaAdsCost > 0 ? ((totals.backOfficeRevenue + totals.analyticsRevenue) / (totals.googleAdsCost + totals.metaAdsCost)).toFixed(1) : 0}x
                    </td>
                  </tr>
                  
                  {/* 2d Average ROAS Row */}
                  <tr className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 text-xs font-semibold text-slate-700 sticky left-0 bg-white z-10 border-r border-gray-200">
                      2d Avg ROAS
                    </td>
                    {hourlyTrendsData.map((data) => (
                      <td key={`avg-roas-${data.hour}`} className="px-2 py-2 text-center text-xs text-slate-700">
                        {data.historicalAvgRoas !== 'N/A' ? `${data.historicalAvgRoas}x` : '-'}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-center text-xs font-bold text-slate-700 bg-gray-50">
                      {(() => {
                        const validData = hourlyTrendsData.filter(d => d.historicalAvgRoas !== 'N/A');
                        return validData.length > 0 ? (validData.reduce((sum, d) => sum + parseFloat(d.historicalAvgRoas), 0) / validData.length).toFixed(1) : 0;
                      })()}x
                    </td>
                  </tr>
                  
                  {/* Trend & Action Row */}
                  <tr className="border-b-2 border-gray-300 hover:bg-gray-50 bg-yellow-50/30">
                    <td className="px-3 py-2 text-xs font-semibold text-orange-700 sticky left-0 bg-yellow-50 z-10 border-r border-gray-300">
                      AI Action
                    </td>
                    {hourlyTrendsData.map((data) => {
                      if (!data.hasData) {
                        return (
                          <td key={`action-${data.hour}`} className="px-2 py-2 text-center text-gray-300">
                            <div className="flex flex-col items-center gap-0.5">
                              <span className="px-1 py-0.5 rounded text-xs bg-gray-100 text-gray-500">Wait</span>
                            </div>
                          </td>
                        );
                      }
                      
                      const getRecommendationBadge = (type) => {
                        switch(type) {
                          case 'increase': return <span className="px-1 py-0.5 rounded text-xs bg-green-100 text-green-800 font-bold">+15%</span>;
                          case 'decrease': return <span className="px-1 py-0.5 rounded text-xs bg-red-100 text-red-800 font-bold">-15%</span>;
                          default: return <span className="px-1 py-0.5 rounded text-xs bg-gray-100 text-gray-800">Hold</span>;
                        }
                      };
                      
                      return (
                        <td key={`action-${data.hour}`} className="px-2 py-2 text-center">
                          <div className="flex flex-col items-center gap-0.5">
                            {getRecommendationBadge(data.recommendationType)}
                          </div>
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-center text-xs font-bold text-orange-700 bg-yellow-100">
                      {hourlyTrendsData.filter(d => d.recommendationType === 'increase').length} UP | {hourlyTrendsData.filter(d => d.recommendationType === 'decrease').length} DOWN
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Campaign Performance Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Google Ads Campaigns */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center">
                  <img src="/google_ads-512.webp" alt="Google Ads" className="w-6 h-6" />
                </div>
                Google Ads Campaigns
                <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                  Live
                </span>
              </h3>
              <p className="text-sm text-gray-600 mt-1">Real-time campaign performance & budget recommendations</p>
            </div>
            <div className="p-4">
              <div className="space-y-3">
                {[
                  { name: "Penti Sütyen & İç Giyim - Premium", spend: "₺12.5k", yesterdayCost: "₺18.2k", status: "increase", avgRoas: "5.8x" },
                  { name: "Bikini & Mayo Koleksiyonu 2024", spend: "₺8.2k", yesterdayCost: "₺12.8k", status: "increase", avgRoas: "6.2x" },
                  { name: "Penti Pijama & Ev Giyim", spend: "₺6.8k", yesterdayCost: "₺9.9k", status: "maintain", avgRoas: "4.1x" },
                  { name: "Çorap & Külotlu Çorap", spend: "₺5.4k", yesterdayCost: "₺8.1k", status: "maintain", avgRoas: "3.9x" },
                  { name: "Büyük Beden Koleksiyonu", spend: "₺4.1k", yesterdayCost: "₺6.3k", status: "increase", avgRoas: "5.3x" },
                  { name: "Yoga & Aktif Giyim", spend: "₺3.7k", yesterdayCost: "₺5.8k", status: "decrease", avgRoas: "2.1x" },
                  { name: "Bralet & Sporcu Sütyeni", spend: "₺2.9k", yesterdayCost: "₺4.4k", status: "maintain", avgRoas: "3.7x" },
                  { name: "Düğün Koleksiyonu - Premium", spend: "₺2.3k", yesterdayCost: "₺3.5k", status: "increase", avgRoas: "5.9x" }
                ].map((campaign, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 text-sm">{campaign.name}</div>
                      <div className="flex items-center gap-3 mt-1">
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Current:</span>
                            <span className="text-lg font-bold text-blue-600">{campaign.spend}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Yesterday:</span>
                            <span className="text-sm font-medium text-gray-600">{campaign.yesterdayCost}</span>
                          </div>
                        </div>
                        <span className="px-2 py-1 rounded text-xs bg-slate-100 text-slate-700 font-medium">
                          2d avg: {campaign.avgRoas}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      {campaign.status === 'increase' && (
                        <div className="flex items-center gap-1">
                          <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-800 font-bold">+25%</span>
                        </div>
                      )}
                      {campaign.status === 'maintain' && (
                        <div className="flex items-center gap-1">
                          <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-800">Hold</span>
                        </div>
                      )}
                      {campaign.status === 'decrease' && (
                        <div className="flex items-center gap-1">
                          <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-800 font-bold">-20%</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Meta Ads Campaigns */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center">
                  <img src="/6033716.png" alt="Meta" className="w-6 h-6" />
                </div>
                Meta Ads Campaigns
                <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                  Live
                </span>
              </h3>
              <p className="text-sm text-gray-600 mt-1">Facebook & Instagram campaign optimization</p>
            </div>
            <div className="p-4">
              <div className="space-y-3">
                {[
                  { name: "Instagram Stories - Bikini Showcase", spend: "₺9.3k", yesterdayCost: "₺14.1k", status: "increase", avgRoas: "6.1x" },
                  { name: "Facebook Video - Yaz Koleksiyonu", spend: "₺7.8k", yesterdayCost: "₺11.5k", status: "increase", avgRoas: "5.7x" },
                  { name: "Instagram Reels - İç Giyim Trends", spend: "₺6.2k", yesterdayCost: "₺9.4k", status: "maintain", avgRoas: "4.3x" },
                  { name: "Facebook Carousel - Pijama Takımı", spend: "₺5.5k", yesterdayCost: "₺8.2k", status: "maintain", avgRoas: "3.8x" },
                  { name: "Instagram Feed - Anne Koleksiyonu", spend: "₺4.7k", yesterdayCost: "₺7.1k", status: "increase", avgRoas: "5.4x" },
                  { name: "Facebook Retargeting - Sepet Terk", spend: "₺3.9k", yesterdayCost: "₺6.3k", status: "decrease", avgRoas: "1.9x" },
                  { name: "Instagram Shopping - Push Up", spend: "₺3.2k", yesterdayCost: "₺4.8k", status: "maintain", avgRoas: "4.0x" },
                  { name: "Facebook Lookalike - Premium", spend: "₺2.8k", yesterdayCost: "₺4.1k", status: "increase", avgRoas: "5.2x" }
                ].map((campaign, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 text-sm">{campaign.name}</div>
                      <div className="flex items-center gap-3 mt-1">
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Current:</span>
                            <span className="text-lg font-bold text-purple-600">{campaign.spend}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Yesterday:</span>
                            <span className="text-sm font-medium text-gray-600">{campaign.yesterdayCost}</span>
                          </div>
                        </div>
                        <span className="px-2 py-1 rounded text-xs bg-slate-100 text-slate-700 font-medium">
                          2d avg: {campaign.avgRoas}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      {campaign.status === 'increase' && (
                        <div className="flex items-center gap-1">
                          <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-800 font-bold">+25%</span>
                        </div>
                      )}
                      {campaign.status === 'maintain' && (
                        <div className="flex items-center gap-1">
                          <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-800">Hold</span>
                        </div>
                      )}
                      {campaign.status === 'decrease' && (
                        <div className="flex items-center gap-1">
                          <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-800 font-bold">-20%</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
       </div>
     </div>
   );
 } 